/**
 * Editor Settings Panel Component
 * Shows settings for the currently selected element
 */
import React from "react";
import { useEditor } from "@craftjs/core";
import { Box, IconButton, Typography, Chip, Tooltip } from "@mui/material";
import { Delete as DeleteIcon, Settings as SettingsIcon } from "@mui/icons-material";
import { componentIcons } from "../editorConstants";

export const EditorSettingsPanel: React.FC = () => {
  const { selected, actions, query } = useEditor((state) => {
    const currentNodeId = Array.from(state.events.selected)[0];
    let selected;

    if (currentNodeId) {
      selected = {
        id: currentNodeId,
        name: state.nodes[currentNodeId].data.displayName || state.nodes[currentNodeId].data.name,
        settings: state.nodes[currentNodeId].related?.settings,
        isDeletable: state.nodes[currentNodeId].data.name !== "RootContainer" && (() => {
          const parentId = state.nodes[currentNodeId].data.parent;
          if (parentId && state.nodes[parentId]) {
            const linkedNodes = state.nodes[parentId].data.linkedNodes || {};
            return !Object.values(linkedNodes).includes(currentNodeId);
          }
          return true;
        })(),
        type: state.nodes[currentNodeId].data.name,
        parent: state.nodes[currentNodeId].data.parent,
      };
    }

    return { selected };
  });

  if (!selected) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            background: "linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%)",
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <SettingsIcon sx={{ fontSize: 40, color: "primary.light" }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={600} color="text.primary" gutterBottom>
          No Element Selected
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
          Click on any element in the canvas or select from the Layers panel to edit its properties
        </Typography>
        <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 1 }}>
          <Chip label="Click" size="small" sx={{ bgcolor: "grey.100" }} />
          <Typography variant="caption" color="text.secondary">
            to select
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100% - 40px)" }}>
      {/* Enhanced Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          background: "linear-gradient(to right, #fafafa, #fff)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              bgcolor: "primary.light",
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
            }}
          >
            {componentIcons[selected.type] || "📦"}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {selected.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selected.type}
            </Typography>
          </Box>
          {selected.isDeletable && (
            <Tooltip title="Delete element" arrow>
              <IconButton
                onClick={() => {
                  if (window.confirm("Delete this element?")) {
                    const nodeId = selected.id;
                    const parentId = selected.parent;
                    actions.delete(nodeId);
                    if (parentId) {
                      requestAnimationFrame(() => {
                        actions.selectNode(parentId);
                      });
                    }
                  }
                }}
                sx={{
                  color: "grey.500",
                  "&:hover": { color: "error.main", bgcolor: "error.light" },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Settings Content */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {selected.settings ? (
          <Box sx={{ p: 2 }}>{React.createElement(selected.settings)}</Box>
        ) : (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography sx={{ fontSize: 24, mb: 1 }}>⚙️</Typography>
            <Typography variant="body2" color="text.secondary">
              No settings available for this element
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EditorSettingsPanel;
